'use client';

import { useState, useCallback, memo } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import i18nConfig from '@i18nConfig';
import { Locales } from '@/types/i18n';
import styles from './LanguageSwitcher.module.css';
import ChevronDownIcon from '@/components/ui/icons/ChevronDownIcon';
import GlobeIcon from '@/components/ui/icons/GlobeIcon';
import { setLocaleCookie } from '@/lib/i18n/setLocalCookie';
import { getNewPathname } from '@/lib/i18n/getNewPathName';

// Define a type for the language object based on i18nConfig structure
type Language = {
  code: string;
  label: string;
};

// Isolated component that only uses the useTranslation hook
const TranslationProvider = memo(({ children }: { children: (data: {
  currentLocale: Locales, 
  currentLanguage: Language | undefined, 
  otherLanguages: Language[]
}) => React.ReactNode }) => {
  // Only call useTranslation here
  const { i18n } = useTranslation();
  
  // Derived state
  const currentLocale = i18n?.language as Locales || 'en';
  const allLanguages = i18nConfig.languages;
  const otherLanguages = allLanguages.filter(l => l.code !== currentLocale);
  const currentLanguage = allLanguages.find(l => l.code === currentLocale);
  
  return <>{children({ currentLocale, currentLanguage, otherLanguages })}</>;
});

// Add display name to the component
TranslationProvider.displayName = 'TranslationProvider';

// Main component that doesn't directly use useTranslation
export default function LanguageSwitcher() {
  // Only use useState and useRouter here
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const router = useRouter();
  
  // Event handlers
  const toggleDropdown = useCallback(() => {
    setIsDropdownVisible(prev => !prev);
  }, []);
  
  // Switch language and preserve ?market param
  const handleSwitch = useCallback((code: string, currentLocale: Locales) => {
    const localeCode = code as Locales;
    setLocaleCookie(localeCode);
    let newPath = getNewPathname(localeCode, currentLocale);
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const market = params.get('market');
      if (market) {
        newPath += (newPath.includes('?') ? '&' : '?') + `market=${market}`;
      }
    }
    router.push(newPath);
    setIsDropdownVisible(false);
  }, [router]);
  
  return (
    <TranslationProvider>
      {({ currentLocale, otherLanguages }) => (
        <div className={styles.dropdown}>
          <div
            className={styles.dropdownTrigger}
            onClick={toggleDropdown}
            aria-haspopup="true"
            aria-controls="language-dropdown"
          >
            <div className={styles.button}>
              <GlobeIcon className={styles.globeIcon} />
              <ChevronDownIcon />
            </div>
          </div>
          {isDropdownVisible && (
            <div className={styles.dropdownMenu} id="language-dropdown" role="menu">
              <div>
                {otherLanguages.map((lang) => (
                  <p key={lang.code} onClick={() => handleSwitch(lang.code as Locales, currentLocale)}>
                    {lang.label}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </TranslationProvider>
  );
}
